import { AxiosUtil } from "@app/utils/AxiosUtil";
import {
    AdditionalCompletionRequestArgumentsInterface,
    ConversationMessageInterface,
    CustomEndpointInterface,
    ModelCardInterface,
    ToolCallInterface,
} from "@dashart/dashart-gpt-shared-library";
import { ChatCompletionResultInterface } from "../interfaces/ChatCompletionResultInterface";
import { ChatServiceInterface } from "../interfaces/ChatServiceInterface";
import { AuthUtil } from "../utils/AuthUtil";
import { AuthService } from "./AuthService";
import { BaseService } from "./BaseService";
import { StreamUtil } from "../utils/StreamUtil";

/**
 * Per-endpoint chat service implementing ChatServiceInterface.
 * Unlike other provider services this is NOT a singleton — one instance per
 * CustomEndpoint, parameterized by its publicId rather than a static apiResourceName.
 */
export interface ToolProgressInterface {
    tool: string;
    emoji?: string;
    label?: string;
    toolCallId: string;
    status: 'running' | 'completed' | 'failed';
}

export class CustomEndpointChatService extends BaseService implements ChatServiceInterface {

    public readonly modelBaseNameStart: string;
    public readonly endpoint: CustomEndpointInterface;

    /**
     * Captures the endpoint and derives a model-name prefix used by history
     * detection. Uses publicId (not numeric id) to keep the prefix non-enumerable.
     *
     * @param endpoint CustomEndpoint this service will operate against
     */
    constructor(endpoint: CustomEndpointInterface) {
        super();
        this.endpoint = endpoint;
        this.modelBaseNameStart = `custom-${endpoint.publicId}`;
    }

    /**
     * Checks if the parsed data is a custom tool progress message.
     * These messages have a different shape than standard SSE chunks.
     *
     * @param data The parsed JSON data
     * @returns true if this is a tool progress message
     */
    private isToolProgressMessage(data: any): data is ToolProgressInterface {
        return data && typeof data === 'object' && 'tool' in data && 'toolCallId' in data && 'status' in data;
    }

    /**
     * Returns the default model (endpoint.defaultModel, falling back to the first
     * configured model) and the publicId-scoped apiResourceName.
     *
     * @returns configuration consumed by BaseService and history detection
     */
    protected getConfiguration(): { defaultModel: string; apiResourceName: string; } {
        return {
            defaultModel: this.endpoint.defaultModel ?? (this.endpoint.models?.[0] ?? ''),
            apiResourceName: `custom/${this.endpoint.publicId}`,
        };
    }

    /**
     * Returns the v2 base path for this endpoint's routes.
     * Internal helper used by every HTTP method.
     *
     * @returns `/v2/custom/{publicId}`
     */
    private getBasePath(): string {
        return `/v2/custom/${this.endpoint.publicId}`;
    }

    /**
     * Checks whether the backend confirms the endpoint is reachable for the user.
     * Backed by GET /v2/custom/:publicId/service-available.
     *
     * @returns Promise resolving to the `available` flag (false on any failure)
     */
    public async getServiceAvailableForUser(): Promise<boolean> {
        const response = await this.axiosInstance.get(
            `${this.getBasePath()}/service-available`,
            { headers: this.getDefaultHeaders() }
        );
        await this.checkResponseStatus(response);
        return (response.data?.available as boolean) ?? false;
    }

    /**
     * Loads available models from the backend, falling back to the endpoint's
     * locally configured models if the server returns none.
     * Backed by GET /v2/custom/:publicId/models.
     *
     * @returns Promise resolving to an array of ModelCardInterface entries
     */
    public async getAvailableModels(): Promise<ModelCardInterface[]> {
        const response = await this.axiosInstance.get(
            `${this.getBasePath()}/models`,
            { headers: this.getDefaultHeaders() }
        );
        await this.checkResponseStatus(response);
        const modelIds = (response.data?.models ?? this.endpoint.models ?? []) as string[];
        return modelIds.map((id) => this.mapModelIdToCard(id));
    }

    /**
     * Alias for getAvailableModels — custom endpoints don't distinguish chat vs.
     * other model types. Required by ChatServiceInterface.
     *
     * @returns Promise resolving to chat-capable model cards
     */
    public async getAvailableChatModels(): Promise<ModelCardInterface[]> {
        return this.getAvailableModels();
    }

    /**
     * Requests a fresh conversation id from the backend.
     * Backed by POST /v2/custom/:publicId/new-conversation-id.
     *
     * @returns Promise resolving to the new conversation id
     */
    public async requestNewConversationId(): Promise<string> {
        const response = await this.axiosInstance.post(
            `${this.getBasePath()}/new-conversation-id`,
            {},
            { headers: this.getDefaultHeaders() }
        );
        await this.checkResponseStatus(response);
        return response.data?.conversationId ?? response.data;
    }

    /**
     * Opens an SSE stream against POST /v2/custom/:publicId?format=stream and
     * forwards chunk/end events to the supplied callbacks. Aborts any prior
     * in-flight request before starting a new one.
     *
     * @param prompt user prompt
     * @param useServerSideHistory when true, server prepends cached history
     * @param previousMessages optional caller-supplied prior messages
     * @param onResponse callback fired for every streamed chunk
     * @param onFinishCallback callback fired once on the final `end` event
     * @param model model id (defaults to endpoint.defaultModel or first configured model)
     * @param maxTokens optional per-request max tokens
     * @param conversationId optional existing conversation id sent via header
     * @param temperature optional sampling temperature
     * @param additionalArguments reserved for provider-specific extras
     * @returns Promise resolving to a `{ cancel }` handle; rejects on transport errors
     */
    public async chatCompletion(
        prompt: string,
        useServerSideHistory?: boolean,
        previousMessages?: ConversationMessageInterface[],
        onResponse?: (data: any, prompt: string, currentCompletion: string, currentReasoning: string) => void,
        onFinishCallback?: (data: any, prompt: string, currentCompletion: string, currentReasoning: string) => void,
        model?: string,
        maxTokens?: number,
        conversationId?: string,
        temperature?: number,
        additionalArguments?: AdditionalCompletionRequestArgumentsInterface,
    ): Promise<{ cancel: Function }> {
        return new Promise<{ cancel: Function }>(async (resolve, reject) => {
            const abortSignalHandler = () => resolveWithCleanup();
            const resolveWithCleanup = (...args: any[]) => cleanup() && resolve(args as any);
            const rejectWithCleanup = (error: any) => cleanup() && reject(error);
            const cleanup = () => {
                if (this.currentAbortController) {
                    this.currentAbortController.signal.removeEventListener("abort", abortSignalHandler);
                    this.currentAbortController = null;
                }
                return true;
            };

            await this.checkSessionValidity();

            if (this.currentAbortController) {
                this.currentAbortController.abort();
            }

            this.currentAbortController = new AbortController();
            this.currentAbortController.signal.addEventListener("abort", abortSignalHandler);

            let callCompleted = false;
            let currentCompletion = ``;
            let currentReasoning = ``;
            const toolCallAccumulator: ToolCallInterface[] = [];
            const toolProgressAccumulator: ToolProgressInterface[] = [];

            const mergeToolCallDelta = (delta: any) => {
                const index = typeof delta?.index === 'number' ? delta.index : toolCallAccumulator.length;
                if (!toolCallAccumulator[index]) {
                    toolCallAccumulator[index] = {
                        id: '',
                        type: 'function',
                        function: { name: '', arguments: '' },
                    };
                }
                const target = toolCallAccumulator[index];
                if (delta.id) target.id = delta.id;
                if (delta.type) target.type = delta.type;
                if (delta.function?.name) target.function.name += delta.function.name;
                if (delta.function?.arguments) target.function.arguments += delta.function.arguments;
            };

            // Agent-style endpoints emit standalone tool progress messages such as
            // {"tool":"search_files","toolCallId":"...","status":"running"} between
            // content chunks. Upsert by toolCallId so status transitions
            // (running -> completed/failed) update the same entry.
            const upsertToolProgress = (progress: ToolProgressInterface) => {
                const existing = toolProgressAccumulator.find((p) => p.toolCallId === progress.toolCallId);
                if (existing) {
                    Object.assign(existing, progress);
                } else {
                    toolProgressAccumulator.push(progress);
                }
            };

            const resolvedModel = model ?? this.getConfiguration().defaultModel;

            try {
                await StreamUtil.postEventStream(
                    `${AxiosUtil.baseURL}${this.getBasePath()}?format=stream`,
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Connection': 'keep-alive',
                            'Cache-Control': 'no-cache',
                            'x-device': AuthUtil.getDeviceId(),
                            'x-conversation-id': conversationId,
                            'Authorization': `Bearer ${AuthUtil.getAccessToken()}`,
                            'accept': "text/event-stream",
                            'Transfer-Encoding': "chunked"
                        },
                        body: JSON.stringify({
                            model: resolvedModel,
                            maxTokens: maxTokens,
                            prompt: prompt,
                            useServerSideHistory: useServerSideHistory,
                            previousMessages: previousMessages ?? [],
                            temperature: temperature ?? undefined,
                            additionalArguments: additionalArguments ?? {},
                        }),
                        signal: this.currentAbortController.signal,
                        onOpen: async (response) => {
                            const contentType = response.headers.get('content-type') ?? '';
                            if (!response.ok || !contentType.toLowerCase().startsWith(StreamUtil.EventStreamContentType)) {
                                const responseBody = await response.text();
                                throw new Error(`Unexpected status code: ${response.status}, ${response.statusText}, ${responseBody}`);
                            }
                        },
                        onMessage: (msg) => {
                            try {
                                if (!msg.data || msg.data.trim() === '') {
                                    return;
                                }
                                if (msg.data?.toLocaleLowerCase() === '[done]') {
                                    return;
                                }
                                if (msg.event === 'error') {
                                    rejectWithCleanup(new Error(`Server error: ${msg.data}`));
                                    return;
                                }

                                const data = JSON.parse(msg.data) as ChatCompletionResultInterface;

                                // Agent-style tool progress messages
                                if (this.isToolProgressMessage(data)) {
                                    upsertToolProgress(data as unknown as ToolProgressInterface);
                                    (data as any).toolProgress = toolProgressAccumulator.map((p) => ({ ...p }));
                                }

                                if (msg.event === 'chunk' || msg.event === 'message' || msg.event === '') {
                                    const delta = data.choices?.[0]?.delta;
                                    currentCompletion += delta?.content ?? '';
                                    currentReasoning += delta?.reasoning_content ?? delta?.reasoning ?? '';

                                    // Merge OpenAI-style streaming tool_call deltas
                                    // (data.choices[0].delta.tool_calls) into the accumulator
                                    // so the live view shows tool calls while streaming.
                                    const deltaToolCalls = (data.choices?.[0]?.delta as any)?.tool_calls as any[] | undefined;
                                    if (deltaToolCalls?.length) {
                                        deltaToolCalls.forEach(mergeToolCallDelta);
                                    }

                                    if (toolCallAccumulator.length > 0) {
                                        (data as any).aggregatedToolCalls = toolCallAccumulator.map((tc) => ({
                                            ...tc,
                                            function: { ...tc.function },
                                        }));
                                    }

                                    onResponse?.(data, prompt, currentCompletion, currentReasoning);
                                } else if (msg.event === 'end') {
                                    const firstChoice = data.choices[0];
                                    let completion = firstChoice?.message?.content ?? '';
                                    if (!completion || completion.length === 0) {
                                        completion = currentCompletion;
                                    }

                                    // The backend sends the assembled tool_calls on the final
                                    // end payload; when it does, treat it as authoritative and
                                    // replace any partially assembled calls.
                                    const finalToolCalls = (firstChoice?.message as any)?.tool_calls as ToolCallInterface[] | undefined;
                                    if (finalToolCalls?.length) {
                                        toolCallAccumulator.length = 0;
                                        finalToolCalls.forEach((tc) => toolCallAccumulator.push(tc));
                                    }

                                    const aggregatedSource = toolCallAccumulator.length > 0 ? toolCallAccumulator : undefined;
                                    (data as any).aggregatedToolCalls = aggregatedSource
                                        ? aggregatedSource.map((tc) => ({
                                            ...tc,
                                            function: { ...tc.function },
                                        }))
                                        : undefined;

                                    // Attach final progress so it can be persisted into the
                                    // message history, then reset per-request accumulators so a
                                    // follow-up request does not inherit the previous turn's state.
                                    if (toolProgressAccumulator.length > 0) {
                                        (data as any).toolProgress = toolProgressAccumulator.map((p) => ({ ...p }));
                                    }
                                    toolProgressAccumulator.length = 0;

                                    onFinishCallback?.(data, prompt, completion, currentReasoning);
                                    resolveWithCleanup(completion);
                                    callCompleted = true;
                                }
                            } catch (error) {
                                console.error(error);
                            }
                        },
                        onClose: () => {
                            if (!callCompleted) {
                                rejectWithCleanup(new Error('Connection closed unexpectedly'));
                            }
                        },
                    }
                );
            } catch (error) {
                rejectWithCleanup(error);
            }
        });
    }

    /**
     * Builds a ModelCardInterface from a bare model id with sensible defaults.
     * Internal helper for getAvailableModels.
     *
     * @param id model identifier
     * @returns a ModelCardInterface populated with default capabilities
     */
    private mapModelIdToCard(id: string): ModelCardInterface {
        return {
            id,
            displayName: id,
            modelType: 'custom' as any,
            supportsVision: true,
            supportsSystemPrompt: true,
            supportsTemperature: true,
            costPerInputToken: 0,
            costPerOutputToken: 0,
            costPerReasoningToken: 0,
            contextWindow: 0,
            capabilities: [],
            limitations: [],
        } as ModelCardInterface;
    }
}
